// Realtime transcription expects 16-bit little-endian mono PCM at 24 kHz.
export const LIVE_PCM_SAMPLE_RATE = 24000;

// `offset` is the read position of the next output sample relative to the
// start of the next input chunk. It is always above -1; a negative offset
// falls between `last`, the final sample of the previous chunk, and the first
// sample of the next one, so interpolation continues across chunk boundaries.
export type ResamplerState = { offset: number; last: number };

export const INITIAL_RESAMPLER_STATE: ResamplerState = { offset: 0, last: 0 };

const toPcm16 = (sample: number) => {
  const clamped = Math.max(-1, Math.min(1, sample));
  return Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
};

// Linear interpolation from the microphone rate to the target rate. Speech
// energy above 12 kHz is negligible, so the missing low-pass filter does not
// matter for recognition.
export const resamplePcm = ({
  input,
  inputRate,
  outputRate = LIVE_PCM_SAMPLE_RATE,
  state = INITIAL_RESAMPLER_STATE,
}: {
  input: Float32Array;
  inputRate: number;
  outputRate?: number;
  state?: ResamplerState;
}): { output: Int16Array; state: ResamplerState } => {
  if (!input.length) {
    return { output: new Int16Array(0), state };
  }
  const step = inputRate / outputRate;
  const count = Math.max(
    0,
    Math.floor((input.length - 1 - state.offset) / step) + 1,
  );
  const sampleAt = (index: number) => (index < 0 ? state.last : input[index]);
  const output = Int16Array.from({ length: count }, (_, outputIndex) => {
    const position = state.offset + outputIndex * step;
    const index = Math.floor(position);
    const fraction = position - index;
    const sample =
      fraction === 0
        ? sampleAt(index)
        : sampleAt(index) + (sampleAt(index + 1) - sampleAt(index)) * fraction;
    return toPcm16(sample);
  });
  return {
    output,
    state: {
      offset: state.offset + count * step - input.length,
      last: input[input.length - 1],
    },
  };
};

// Root mean square on a 0..1 scale, used to tell speech from silence.
export const measurePcm16Level = (samples: Int16Array) =>
  samples.length
    ? Math.sqrt(
        samples.reduce((sum, sample) => sum + (sample / 0x8000) ** 2, 0) /
          samples.length,
      )
    : 0;

const BASE64_CHUNK_BYTES = 0x8000;

// Raw sample bytes, no container header, explicitly little-endian.
export const encodePcm16LittleEndian = (samples: Int16Array) => {
  const view = new DataView(new ArrayBuffer(samples.length * 2));
  samples.forEach((sample, index) => view.setInt16(index * 2, sample, true));
  return view.buffer;
};

export const encodePcm16Base64 = (samples: Int16Array) => {
  const bytes = new Uint8Array(encodePcm16LittleEndian(samples));
  const binary = Array.from(
    { length: Math.ceil(bytes.length / BASE64_CHUNK_BYTES) },
    (_, chunk) =>
      String.fromCharCode(
        ...bytes.subarray(
          chunk * BASE64_CHUNK_BYTES,
          (chunk + 1) * BASE64_CHUNK_BYTES,
        ),
      ),
  ).join('');
  return btoa(binary);
};
